import {createFeatureSelector, createSelector} from "@ngrx/store";
import {ISecurityState} from "../reducers/security.reducer";
import {DbStoreState} from "../reducers";
import {SecurityIssue} from "../../websites/website/security/security-issue";
import {SecurityIssueMapper} from "../../websites/website/security/security-issue-mapper";
import {SecurityVulnerability} from "../../websites/website/security/security-vulnerability";
import {SecurityVulnerabilityMapper} from "../../websites/website/security/security-vulnerability-mapper";
import {TypeUtils} from "../../shared/utils/type-utils";
import {SecurityIgnoredIssueMapper} from "../../websites/website/security/security-ignored-issue-mapper";
import {SecurityScanMapper} from "../../websites/website/security/security-scan-mapper";
import {SecuritySettingsMapper} from "../../websites/website/security/security-settings-mapper";
import {SecurityIgnoredIssue} from '../../websites/website/security/security-ignored-issue';
import {SecurityScheduleMapper} from "../../websites/website/security/security-schedule-mapper";
import {SecurityAutoupdateMapper} from "../../websites/website/security/security-autoupdate-mapper";

const getDbStoreState = createFeatureSelector<DbStoreState>('dbStore');

export const getSecurityState = createSelector(getDbStoreState, (dbStoreState: DbStoreState) => dbStoreState.security);

export const getSecurityScan = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityScan && state.entities[websiteId].securityScan.securityScan ? SecurityScanMapper.map(state.entities[websiteId].securityScan.securityScan) : null
);

export const getSecurityScanIssues = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) => {
    const securityScanData: any = state.entities[websiteId] && state.entities[websiteId].securityScan ? state.entities[websiteId].securityScan.securityScan : null;

    if (!securityScanData) {
      return [];
    }

    let issues: SecurityIssue[] = [];
    securityScanData.logs.forEach((logData: any) => {
      if (['plugin_issues', 'theme_issues', 'core_issues'].includes(logData.name)) {
        const diffType: string = logData.name.split('_issues').join('');
        issues = issues.concat(logData.data.map((data) => SecurityIssueMapper.map(data, logData.name, diffType)));
      }
    });

    return issues;
  }
);

export const getSecurityScanVulnerabilities = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) => {
    const securityScanData: any = state.entities[websiteId] && state.entities[websiteId].securityScan ? state.entities[websiteId].securityScan.securityScan : null;

    if (!securityScanData) {
      return [];
    }

    const vulnerabilitiesDatas: any[] = securityScanData.logs.reduce((vulnerabilityDatas: any[], logData: any) => {
      if (logData.name === 'plugin_vulnerabilities') {
        const pluginsVulnerabilities: any[] = logData.data || [];
        Object.values(pluginsVulnerabilities).forEach((pluginVulnerabilitiesData: any) => {
          vulnerabilityDatas = vulnerabilityDatas.concat(JSON.parse(pluginVulnerabilitiesData.vulnerabilities));
        });
      }
      else if (logData.name === 'theme_vulnerabilities') {
        const themesVulnerabilities: any[] = logData.data || [];
        Object.values(themesVulnerabilities).forEach((themeVulnerabilitiesData: any) => {
          vulnerabilityDatas = vulnerabilityDatas.concat(JSON.parse(themeVulnerabilitiesData.vulnerabilities));
        });
      }
      else if (logData.name === 'core_vulnerabilities') {
        let coreVulnerabilities: any[];
        if (logData.data) {
          if (TypeUtils.isString(logData.data.vulnerabilities)) {
            coreVulnerabilities = JSON.parse(logData.data.vulnerabilities);
          }
          else {
            coreVulnerabilities = logData.data.vulnerabilities || [];
          }
        }
        vulnerabilityDatas = vulnerabilityDatas.concat(coreVulnerabilities);
      }

      return vulnerabilityDatas;
    }, []);

    const vulnerabilities: SecurityVulnerability[] = vulnerabilitiesDatas.map(SecurityVulnerabilityMapper.map);

    return vulnerabilities;
  }
);

export const getSecurityScanRunning = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityScan ? state.entities[websiteId].securityScan.running : false
);

export const getSecurityScanLoading = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityScan ? state.entities[websiteId].securityScan.loading : false
);

export const getSecurityScanLoaded = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityScan ? state.entities[websiteId].securityScan.loaded : false
);

export const getSecurityIgnoredIssues = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) => {
    const securityIgnoredIssuesData: any = state.entities[websiteId] && state.entities[websiteId].securityIgnoredIssues ? state.entities[websiteId].securityIgnoredIssues.securityIgnoredIssues : null;

    if (!securityIgnoredIssuesData) {
      return [];
    }

    const ignoredIssues: SecurityIgnoredIssue[] = securityIgnoredIssuesData.map(SecurityIgnoredIssueMapper.map);

    return ignoredIssues;
  }
);

export const getSecurityIgnoredIssuesLoading = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityIgnoredIssues ? state.entities[websiteId].securityIgnoredIssues.loading : false
);

export const getSecurityIgnoredIssuesLoaded = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityIgnoredIssues ? state.entities[websiteId].securityIgnoredIssues.loaded : false
);

export const getSecuritySettings = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securitySettings ? SecuritySettingsMapper.map(state.entities[websiteId].securitySettings.securitySettings) : null
);

export const getSecuritySettingsLoading = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securitySettings ? state.entities[websiteId].securitySettings.loading : false
);

export const getSecuritySettingsLoaded = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securitySettings ? state.entities[websiteId].securitySettings.loaded : false
);

export const getSecuritySchedule = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securitySchedule ? SecurityScheduleMapper.map(state.entities[websiteId].securitySchedule.securitySchedule) : null
);

export const getSecurityScheduleLoading = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securitySchedule ? state.entities[websiteId].securitySchedule.loading : false
);

export const getSecurityScheduleLoaded = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securitySchedule ? state.entities[websiteId].securitySchedule.loaded : false
);

export const getSecurityAutoupdate = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityAutoupdate ? SecurityAutoupdateMapper.map(state.entities[websiteId].securityAutoupdate.securityAutoupdate) : null
);

export const getSecurityAutoupdateLoading = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityAutoupdate ? state.entities[websiteId].securityAutoupdate.loading : false
);

export const getSecurityAutoupdateLoaded = (websiteId: number) => createSelector(getSecurityState,
  (state: ISecurityState) =>
    state.entities[websiteId] && state.entities[websiteId].securityAutoupdate ? state.entities[websiteId].securityAutoupdate.loaded : false
);
